import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AgencyHomePromoMessage from '../components/AgencyHomePromoMessage';
import { useAuth } from '../components/AuthContext';
import { useOrganizationInDb } from '../hooks/useApiQueries';
import { useLeadsVoAgent, useRequestLeadsVoActivation } from '../hooks/useLeadsVoQueries';

interface Universe {
  key: string;
  icon: string;
  title: string;
  description: string;
  path: string;
  enabled: boolean;
  disabledLabel?: string;
}

const AgencyHomePage = () => {
  const auth = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const currentUser = auth ? auth.currentUser : null;
  const orgId = currentUser?.data?.organization?.value;

  const { data: organizationInfo } = useOrganizationInDb(orgId);
  const franchiseIdRaw =
    currentUser?.data?.organization_franchise_id ??
    organizationInfo?.franchise_id ??
    organizationInfo?.bf580a2a61f8c16cae1aa13670de6fa21184592f;
  const franchiseId = franchiseIdRaw != null ? String(franchiseIdRaw) : undefined;

  const { isSuccess: leadsVoEnabled } = useLeadsVoAgent();
  const requestActivation = useRequestLeadsVoActivation();
  const [activationDialogOpen, setActivationDialogOpen] = useState(false);

  const EWIGO_FRANCHISE_ID = '37';
  const isEwigoAgency = franchiseId === EWIGO_FRANCHISE_ID;

  const handleLeadsVoCta = async () => {
    if (leadsVoEnabled) {
      navigate('/leads-vo/packs');
      return;
    }
    // Pas encore activé : envoie la demande à l'admin puis ouvre la confirmation.
    // On ouvre la dialog dans tous les cas — même si le clic part en erreur ou si la
    // demande a déjà été envoyée, l'utilisateur voit un message rassurant.
    try {
      await requestActivation.mutateAsync();
    } catch {
      /* la mutation log l'erreur côté backend ; on affiche la confirmation quand même */
    }
    setActivationDialogOpen(true);
  };

  const allUniverses: Universe[] = [
    {
      key: 'leads-vo',
      icon: '📅',
      title: 'Rendez-vous qualifiés',
      description:
        'Des acheteurs de votre secteur, prêts à vendre leur véhicule. Livrés dans votre agenda. Packs pré-payés à 35 € HT / RDV.',
      path: '/leads-vo/packs',
      enabled: leadsVoEnabled,
      disabledLabel: 'Sur activation',
    },
    {
      key: 'reprise',
      icon: '🚗',
      title: 'Reprise & Portage de stock',
      description:
        'Ne perdez plus une vente à cause d’une reprise. On rachète ou porte le stock financièrement — à vous de choisir.',
      path: '/reprise',
      enabled: true,
    },
    {
      key: 'admin',
      icon: '📋',
      title: 'Démarches administratives',
      description: 'Carte grise et déclaration d’achat gérées en quelques clics.',
      path: '/list-cg',
      enabled: true,
    },
    {
      key: 'warranty',
      icon: '🛡️',
      title: 'Garanties',
      description: 'Protégez vos ventes avec une garantie mécanique posée depuis votre espace.',
      path: '#',
      enabled: false,
      disabledLabel: 'Bientôt',
    },
  ];

  // La carte Garanties est masquée pour les agences EWIGO (franchise 37).
  const universes = allUniverses.filter((u) => !(isEwigoAgency && u.key === 'warranty'));

  const agencyName = currentUser?.data?.organization?.name;

  return (
    <Box sx={{ minHeight: '100%', background: 'linear-gradient(180deg, #FAFAFA 0%, #FFFFFF 100%)' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>
        {/* Hero */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <Typography sx={{ fontWeight: 900, fontSize: { xs: 26, md: 38 }, color: '#262626', letterSpacing: '-0.5px', lineHeight: 1.1 }}>
            Bonjour{agencyName ? ` ${agencyName}` : ''}
          </Typography>
        </Box>

        {/* ===== MOBILE : boutons full-width type app native ===== */}
        {isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {universes.map((u) => (
              <Box
                key={u.key}
                onClick={() => u.enabled && navigate(u.path)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: '#fff',
                  border: '1px solid #EFEFEF',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                  opacity: u.enabled ? 1 : 0.6,
                  position: 'relative',
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                  transition: 'transform 0.1s ease, background-color 0.1s ease',
                  '&:active': u.enabled ? { transform: 'scale(0.98)', backgroundColor: '#FFF9EF' } : {},
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    flexShrink: 0,
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 28,
                    background: 'linear-gradient(135deg, #FFF6E8 0%, #FFEFD2 100%)',
                  }}
                >
                  {u.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#262626' }}>{u.title}</Typography>
                    {!u.enabled && (
                      <Chip
                        label={u.disabledLabel ?? 'Bientôt'}
                        size="small"
                        sx={{ backgroundColor: '#F0F0F0', color: '#9E9E9E', fontWeight: 600, fontSize: 10, height: 18 }}
                      />
                    )}
                  </Box>
                  <Typography sx={{ color: '#8A8A8A', fontSize: 12.5, lineHeight: 1.4, mt: 0.25 }}>
                    {u.description}
                  </Typography>
                </Box>
                {u.enabled && <ChevronRightIcon sx={{ color: '#C4C4C4', flexShrink: 0 }} />}
              </Box>
            ))}
          </Box>
        ) : (
          /* ===== DESKTOP : cartes horizontales avec hover ===== */
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2.5, alignItems: 'stretch' }}>
            {universes.map((u) => (
              <Box
                key={u.key}
                onClick={() => u.enabled && navigate(u.path)}
                sx={{
                  flex: 1,
                  p: 3,
                  borderRadius: 4,
                  backgroundColor: '#fff',
                  border: '1px solid #EFEFEF',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  cursor: u.enabled ? 'pointer' : 'default',
                  opacity: u.enabled ? 1 : 0.65,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: 'linear-gradient(90deg, #FDBB5F, #FDD15F)',
                    transform: 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'transform 0.25s ease',
                  },
                  ...(u.enabled && {
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 16px 32px rgba(253,187,95,0.22)',
                      borderColor: '#FDBB5F',
                    },
                    '&:hover::after': { transform: 'scaleX(1)' },
                    '&:hover .universe-arrow': { opacity: 1, transform: 'translateX(0)' },
                    '&:hover .universe-icon': { transform: 'scale(1.08)' },
                  }),
                }}
              >
                {!u.enabled && (
                  <Chip
                    label={u.disabledLabel ?? 'Bientôt'}
                    size="small"
                    sx={{ position: 'absolute', top: 10, right: 10, backgroundColor: '#F0F0F0', color: '#9E9E9E', fontWeight: 600, fontSize: 10.5, height: 22 }}
                  />
                )}
                <Box
                  className="universe-icon"
                  sx={{
                    width: 72,
                    height: 72,
                    flexShrink: 0,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 34,
                    background: 'linear-gradient(135deg, #FFF6E8 0%, #FFEFD2 100%)',
                    mb: 2,
                    transition: 'transform 0.25s ease',
                  }}
                >
                  {u.icon}
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#262626', mb: 0.5 }}>{u.title}</Typography>
                <Typography sx={{ color: '#8A8A8A', fontSize: 13, lineHeight: 1.45 }}>{u.description}</Typography>
                {u.enabled && (
                  <Typography
                    className="universe-arrow"
                    sx={{
                      mt: 1.5,
                      color: '#FDBB5F',
                      fontWeight: 700,
                      fontSize: 14,
                      opacity: 0,
                      transform: 'translateX(-6px)',
                      transition: 'opacity 0.25s ease, transform 0.25s ease',
                    }}
                  >
                    Accéder →
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Section promotionnelle */}
        <Box
          sx={{
            mt: { xs: 4, md: 5 },
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(120deg, #262626 0%, #1c1c1c 100%)',
            color: 'white',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3,
          }}
        >
          {/* halo décoratif */}
          <Box
            sx={{
              position: 'absolute',
              top: -80,
              right: -40,
              width: 260,
              height: 260,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(253,187,95,0.25) 0%, rgba(253,187,95,0) 70%)',
              pointerEvents: 'none',
            }}
          />
          <Box sx={{ textAlign: { xs: 'center', md: 'left' }, zIndex: 1 }}>
            <Chip
              label="Nouveau service"
              size="small"
              sx={{ backgroundColor: 'rgba(253,187,95,0.18)', color: '#FDD15F', fontWeight: 700, fontSize: 11, mb: 1.5 }}
            />
            <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: { xs: 20, md: 26 }, lineHeight: 1.2, mb: 1 }}>
              Des acheteurs prêts à vous confier leur véhicule.
            </Typography>
            <Typography sx={{ color: '#BDBDBD', fontSize: { xs: 13, md: 15 }, maxWidth: 520, lineHeight: 1.5 }}>
              Recevez des particuliers de votre secteur déjà décidés à vendre — directement dans votre agenda. Packs
              pré-payés à 35 € HT par RDV qualifié.
            </Typography>
          </Box>
          <Button
            onClick={handleLeadsVoCta}
            disabled={requestActivation.isPending}
            sx={{
              zIndex: 1,
              backgroundColor: '#FDBB5F',
              color: '#262626',
              fontWeight: 700,
              fontSize: 15,
              textTransform: 'none',
              px: 4,
              py: 1.5,
              borderRadius: 2.5,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              boxShadow: '0 8px 20px rgba(253,187,95,0.35)',
              '&:hover': { backgroundColor: '#fcae4d', transform: 'translateY(-2px)' },
              transition: 'transform 0.2s ease, background-color 0.2s ease',
            }}
          >
            {leadsVoEnabled ? 'Commander un pack' : 'En savoir plus'}
          </Button>
        </Box>

        {/* Message promotionnel franchise existant */}
        {franchiseId && (
          <Box sx={{ mt: 3 }}>
            <AgencyHomePromoMessage franchiseId={franchiseId} slot="home" />
          </Box>
        )}
      </Container>

      <Dialog
        open={activationDialogOpen}
        onClose={() => setActivationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#262626', fontSize: 22 }}>
          Demande bien reçue
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 15, color: '#464646', lineHeight: 1.55 }}>
            Merci pour votre intérêt pour le service <strong>Rendez-vous qualifiés</strong>. Notre équipe a été
            notifiée et reviendra vers vous très rapidement pour finaliser l'activation de votre compte.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setActivationDialogOpen(false)}
            sx={{
              backgroundColor: '#FDBB5F',
              color: '#262626',
              fontWeight: 700,
              textTransform: 'none',
              px: 3,
              '&:hover': { backgroundColor: '#fcae4d' },
            }}
          >
            J'ai compris
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgencyHomePage;
